/**
 * Topbar com breadcrumb + título grande (TH-54205 / M1.E4.S2.2).
 *
 * Substitui o header inline antigo do AppLayout. Estrutura confere com o
 * protótipo (`design-system/prototype/painel-screens.jsx:99-115`):
 *   altura 60px, breadcrumb mono uppercase 11px tracking 0.1em, título
 *   17px weight 600 tracking -0.01em, slot `actions` à direita.
 *
 * Em mobile, o botão hamburger (`onMenuClick`) aparece à esquerda antes
 * do bloco breadcrumb/título — `AppLayout` injeta o handler do drawer.
 *
 * Logout segue temporariamente como ação default à direita (Notas
 * Técnicas da issue: "depois migra para menu do avatar").
 */

import type { ReactNode } from "react";
import { Menu } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { ThemeToggle } from "@/components/ui/ThemeToggle";

export interface TopbarProps {
  /** Breadcrumb opcional, ex.: `OPERAÇÃO / VISÃO GERAL`. */
  breadcrumb?: string;
  /** Título grande da página. */
  title: string;
  /** Slot direito (botões, badges, search). Quando undefined, AppLayout injeta o logout default. */
  actions?: ReactNode;
  /** Handler do botão hamburger (mobile). Se undefined, o botão não é renderizado. */
  onMenuClick?: () => void;
}

export function Topbar({
  breadcrumb,
  title,
  actions,
  onMenuClick,
}: TopbarProps) {
  return (
    <header
      className="flex h-[60px] items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-background)] px-4 md:px-7"
      role="banner"
    >
      {onMenuClick ? (
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          aria-label="Abrir menu"
          onClick={onMenuClick}
        >
          <Menu className="h-4 w-4" />
        </Button>
      ) : null}

      <div className="min-w-0 flex-1">
        {breadcrumb ? (
          <div
            className="font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]"
            data-testid="topbar-breadcrumb"
          >
            {breadcrumb}
          </div>
        ) : null}
        {/* <h1> e não <div>: é o título da página e o único heading de nível 1.
            Leitor de tela navega por headings — sem ele a página não tem âncora.
            O preflight do Tailwind zera margem/tamanho nativos, então o visual
            é idêntico ao do <div> anterior. */}
        <h1
          className="truncate text-[17px] font-semibold tracking-[-0.01em] text-[var(--color-foreground)]"
          data-testid="topbar-title"
        >
          {title}
        </h1>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <ThemeToggle />
        {actions}
      </div>
    </header>
  );
}
